import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ModelsTransformUtil } from '@ddt-web/common';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { AuthenticationManageService } from '@ddt-web/core';
import { CellData, ComponentData, DateFormatter } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { DateUtil, distinctUntilChangedDeep } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ModelListRow, ModelsExperienceFilters, Portfolio } from '../../models';
import { ModelsBrowseFilterPipe } from '../../pipes/models-browse-filter.pipe';
import { ModelsBrowseDataService } from '../../services';
import { ModelsBrowseFilterTransformUtil } from '../../utils/models-browse-filter-transform.util';
import { SearchKeywordUtil } from '../../utils/search-text-filter.util';

// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
@UntilDestroy()
@Component({
	selector: 'mb-family-list',
	templateUrl: './family-list.component.html',
	styleUrls: ['./family-list.component.scss'],
	animations: [
		trigger('expandCollapseAnimation', [
			state('collapsed, void', style({ height: '0', minHeight: '0', overflow: 'hidden' })),
			state('expanded', style({ height: AUTO_STYLE, overflow: AUTO_STYLE })),
			transition('expanded <=> collapsed, void <=> expanded', animate('500ms ease-in-out'))
		])
	]
})
export class FamilyListComponent implements OnInit, OnChanges {
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.REDIRECT_TO_ACT2) private readonly redirectToACT2: boolean = false;

	@Input() public groupId: string;
	@Input() public category: string;
	@Input() public data: ComponentData;
	@Input() public selectedFilters: Array<ModelsExperienceFilters> = [];
	@Input() public familyModelsMapping: { [familyId: string]: string[] };
	@Input() public selectedModelsToCompare: Array<ModelListRow> = [];
	@Input() public sortByLatestTradeDate: boolean = false;
	@Input() public isAuthenticated: boolean = false;
	@Input() public selectedFamilyId: string = '';
	@Input() public searchText: string = '';
	@Output() public readonly selectedModels: EventEmitter<Array<ModelListRow>> = new EventEmitter<Array<ModelListRow>>();
	@Output() public readonly filteredModelsCount: EventEmitter<number> = new EventEmitter<number>();

	public familyList: Array<{ [columnName: string]: CellData }>;
	public userFilterName: string;
	public familyIds$: { [familyId: string]: Observable<string[]> } = {};
	public familyModelsLength: { [familyId: string]: number } = {};
	public dateFormatter = new DateFormatter(this.dateFormat);

	// Accordion Contents Map is being used to keep a track of the index of the accordion selected.
	public accordionState: { [familyId: string]: boolean } = {};
	public appPaths = ApplicationPathConstants.MODELS_BROWSE_PATHS;

	public tealiumConstants = TealiumConstants;
	public sharedApplicationConstants = SharedApplicationConstants;

	constructor(
		private modelsBrowseDataService: ModelsBrowseDataService,
		private storeService: StoreService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private filterPipe: ModelsBrowseFilterPipe,
		private authManagerService: AuthenticationManageService,
		private router: Router,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public ngOnInit(): void {
		// get the fee update signal to reload all expanded family data
		this.applicationStateStoreProxyService
			.getPerformanceState()
			.pipe(untilDestroyed(this), distinctUntilChangedDeep())
			.subscribe(() => {
				Object.keys(this.accordionState).forEach(familyId => {
					if (!!this.accordionState[familyId]) {
						// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
						this.modelsBrowseDataService.fetchFamilyModelsAnalytics(familyId).subscribe();
					}
				});
			});

		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.authManagerService.getUserAuthenticated().subscribe(authenticated => {
			this.isAuthenticated = authenticated;
			if (!!this.selectedFamilyId) {
				this.onToggleAccordion();
				this.router.navigate([], {
					queryParams: { familyId: null },
					queryParamsHandling: 'merge',
					replaceUrl: true
				});
			}
		});
	}

	public ngOnChanges(): void {
		const familyListArr = (this.data.getTabById('family.list')?.tableData?.[0] as any)?.rows ?? [];
		this.familyList = this.filterPipe.transform(familyListArr, this.selectedFilters, 'portfolios');
		const sortParams = {
			asOfDate: 'asOfDate.formattedValue',
			displayOrder: 'displayOrder.formattedValue',
			subType: 'subType.formattedValue',
			tradeFrequency: 'tradeFrequency.formattedValue'
		};
		this.familyList = ModelsTransformUtil.sortFamiliesWithTradeDateOrFrequency(this.familyList, this.groupId, sortParams, this.sortByLatestTradeDate);

		this.familyList = SearchKeywordUtil.searchByKeyword(this.searchText, this.familyList, 'description', true);
		this.getFilteredModelsCount();
		const filterQueryParams = ModelsBrowseFilterTransformUtil.getFilterQueryParam(this.selectedFilters);
		const params = { userFilterName: !isEmpty(filterQueryParams) ? filterQueryParams.join('$') : null };
		this.userFilterName = params.userFilterName;
	}

	public onToggleAccordion(family?: { [columnName: string]: CellData }): void {
		const familyId = !!this.selectedFamilyId ? this.selectedFamilyId : family?.familyId?.formattedValue;
		this.accordionState[familyId] = !this.accordionState[familyId];

		if (this.isAuthenticated) {
			const shouldLoadData = this.accordionState[familyId] && !this.familyIds$[familyId];
			if (shouldLoadData) {
				const familyModelsAnalytics$ = this.modelsBrowseDataService.fetchFamilyModelsAnalytics(familyId).pipe(
					take(1),
					map(modelsLength => {
						this.familyModelsLength[familyId] = +modelsLength ?? 0;
						this.familyIds$[familyId] = of([familyId]);
					})
				);
				const familyModelsHoldings$ = this.modelsBrowseDataService.fetchFamilyHoldings(familyId).pipe(take(1));

				combineLatest([familyModelsAnalytics$, familyModelsHoldings$]).pipe(untilDestroyed(this)).subscribe();
			}
		} else {
			this.familyIds$[familyId] = of([familyId]);
		}
	}

	public viewDetails(familyId: string): void {
		this.tealiumAnalyticsService.triggerToolCompletion(null, TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE, TealiumConstants.MODELS_EXPERIENCE_EVENTS.ADDITIONAL_DETAILS);
		GlobalAnalyticsMapperUtil.setPageName(TealiumConstants.MODELS_EXPERIENCE_SUB_SUB_SECTION_NAMES.FAMILY_DETAILS);
		this.redirectToACT2 && this.redirectToDeepDive(familyId);
	}

	private getFilteredModelsCount(): void {
		let count: number = 0;
		const portfolioFilter = this.selectedFilters.filter(filter => filter.applicability === 'portfolio');
		const selectedFamilies = this.filterPipe.transform(this.familyList, this.selectedFilters, 'portfolios');
		selectedFamilies?.forEach(family => {
			// models mapping per family for the selected tab (BLK or CMS)
			const modelIdsPerSelectedTab = this.familyModelsMapping?.[family.familyId?.formattedValue];
			let listOfModels = (family.portfolios?.rawValue as any[]) || [];
			// filter models on category basis
			listOfModels = modelIdsPerSelectedTab ? listOfModels.filter(model => modelIdsPerSelectedTab.includes(model.id)) : listOfModels;
			const filteredListOfModels = this.filterPipe.transform(listOfModels, portfolioFilter);
			count += filteredListOfModels.filter(modelRow => {
				return (modelRow as unknown as Portfolio)?.description?.toLowerCase().includes(this.searchText);
			}).length;
		});
		this.filteredModelsCount.emit(count);
	}

	private redirectToDeepDive(familyId: string): void {
		const url = ApplicationPathConstants.ACT2_PATHS.DEEP_DIVE_URL.replace('$$familyId$$', familyId);
		window.open(url, '_self');
	}
}
